import { useCallback, useEffect, useRef, useState } from "react";
import { historyFor } from "../lib/conversationHistory";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { ASSISTANT_WEBSOCKET_URL } from "../config/websocket";
import type {
  ChatMessage,
  OpenaiMessageSchema,
  WebsocketResponseSchema,
  AssistantType,
  FileSearchFilters,
} from "../types/assistant";

type UseAssistantWebSocketOptions = {
  authToken: string | null;
  assistantType: AssistantType;
  fileSearchFilters?: FileSearchFilters;
  /**
   * Which model should answer. The worker's Zod schema currently strips this as an
   * unknown key, so selecting a model has no effect until ai-looker-upper grows a
   * model parameter — sending it now keeps the client contract stable.
   */
  model?: string;
  /**
   * Fires when a turn finishes, with what the worker reports actually served it.
   * The requested model and the served one can differ — a dated snapshot, or a
   * fallback — so anything displaying provenance must use this, not the request.
   */
  onTurnComplete?: (info: {
    model?: string;
    provider?: "openai" | "anthropic";
    inputTokens?: number;
    outputTokens?: number;
    elapsedMs: number;
  }) => void;
  onError?: (error: string) => void;
};

/**
 * Hook to manage WebSocket connection to ai-looker-upper
 * Handles message sending/receiving and state management
 */
export function useAssistantWebSocket({
  authToken,
  assistantType,
  fileSearchFilters,
  model,
  onTurnComplete,
  onError,
}: UseAssistantWebSocketOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const previousResponseIdRef = useRef<string | null>(null);
  // Wall-clock for the turn, so a comparison can show which model was slower.
  const turnStartedAtRef = useRef<number | null>(null);
  const currentAssistantMessageIdRef = useRef<string | null>(null);
  // sendMessage must see the transcript as it is at send time. Reading `messages`
  // directly would either capture a stale closure or rebuild the callback on every
  // streamed token.
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const getMessageType = useCallback(() => {
    // Use v2 for projects and correspondence (supports images), webpages for spaces
    switch (assistantType) {
      case "projects":
        return "projects_v2_assistant_query";
      case "correspondence":
        return "correspondence_v2_assistant_query";
      case "spaces":
        return "webpages_assistant_query";
      default:
        return "projects_v2_assistant_query";
    }
  }, [assistantType]);

  const { sendJsonMessage, readyState } = useWebSocket(ASSISTANT_WEBSOCKET_URL, {
    onMessage(event) {
      const data: WebsocketResponseSchema = JSON.parse(event.data);

      switch (data.type) {
        case "processing":
          setIsRunning(true);
          break;

        case "message_delta":
          setIsRunning(true);
          currentAssistantMessageIdRef.current = data.payload.snapshot.id;

          setMessages((prev) => {
            const existingIndex = prev.findIndex(
              (m) => m.id === data.payload.snapshot.id
            );
            const newMessage = snapshotToChatMessage(data.payload.snapshot);

            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = newMessage;
              return updated;
            }
            return [...prev, newMessage];
          });
          break;

        case "message_done":
          setIsRunning(false);
          currentAssistantMessageIdRef.current = null;

          setMessages((prev) => {
            const existingIndex = prev.findIndex(
              (m) => m.id === data.payload.snapshot.id
            );
            const newMessage = snapshotToChatMessage(data.payload.snapshot);

            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = newMessage;
              return updated;
            }
            return [...prev, newMessage];
          });
          break;

        case "response.completed":
          previousResponseIdRef.current = data.payload.response.id;
          {
            const p = data.payload as typeof data.payload & {
              model?: string;
              provider?: "openai" | "anthropic";
              usage?: { inputTokens?: number; outputTokens?: number };
            };
            onTurnComplete?.({
              model: p.model,
              provider: p.provider,
              inputTokens: p.usage?.inputTokens,
              outputTokens: p.usage?.outputTokens,
              elapsedMs: turnStartedAtRef.current ? Date.now() - turnStartedAtRef.current : 0,
            });
          }
          // Attach Braintrust span ID to the last assistant message for feedback
          if (data.payload.span_id) {
            setMessages((prev) => {
              let lastAssistantIdx = -1;
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].role === "assistant") {
                  lastAssistantIdx = i;
                  break;
                }
              }
              if (lastAssistantIdx >= 0) {
                const updated = [...prev];
                updated[lastAssistantIdx] = {
                  ...updated[lastAssistantIdx],
                  spanId: data.payload.span_id,
                };
                return updated;
              }
              return prev;
            });
          }
          setIsRunning(false);
          break;

        case "error":
          setIsRunning(false);
          currentAssistantMessageIdRef.current = null;

          const errorMessage: ChatMessage = {
            id: data.id,
            role: "assistant",
            content: data.message || "An error occurred",
            status: "error",
            createdAt: new Date(),
          };
          setMessages((prev) => [...prev, errorMessage]);
          onError?.(data.message || "An error occurred");
          break;

        case "unauthorized":
          onError?.("Unauthorized - please refresh and try again");
          break;

        case "bad_request":
          onError?.(data.message || "Bad request");
          break;
      }
    },
    shouldReconnect: () => true,
    reconnectAttempts: 5,
    reconnectInterval: 3000,
  });

  const sendMessage = useCallback(
    (text: string, imageUrls?: string[]) => {
      if (!authToken) {
        onError?.("No auth token available");
        return;
      }

      if (readyState !== ReadyState.OPEN) {
        onError?.("WebSocket not connected");
        return;
      }

      // Add user message to state immediately
      const userMessage: ChatMessage = {
        id: `user:${new Date().toISOString()}`,
        role: "user",
        content: text,
        status: "completed",
        createdAt: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      turnStartedAtRef.current = Date.now();
      setIsRunning(true);

      const messagePayload = {
        type: getMessageType(),
        payload: {
          message: text,
          previousResponseId: previousResponseIdRef.current,
          // Claude has no previousResponseId — the thread itself is the state. Sent
          // for every provider; the OpenAI path ignores it and uses its own handle.
          history: historyFor(messagesRef.current),
          ...(imageUrls && imageUrls.length > 0 ? { imageUrls } : {}),
          ...(fileSearchFilters ? { fileSearchFilters } : {}),
          ...(model ? { model } : {}),
        },
        auth: authToken,
      };

      // Send to WebSocket
      sendJsonMessage(messagePayload);
    },
    [authToken, readyState, sendJsonMessage, getMessageType, fileSearchFilters, model, onError]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    previousResponseIdRef.current = null;
    currentAssistantMessageIdRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    // WebSocket doesn't support cancellation directly
    // We just stop showing the running state
    setIsRunning(false);
  }, []);

  return {
    messages,
    setMessages,
    isRunning,
    sendMessage,
    clearMessages,
    cancel,
    readyState,
    isConnected: readyState === ReadyState.OPEN,
  };
}

/**
 * Convert OpenAI snapshot to our ChatMessage format
 */
function snapshotToChatMessage(snapshot: OpenaiMessageSchema): ChatMessage {
  const textContent = snapshot.content
    .filter((c) => c.type === "text")
    .map((c) => c.text.value)
    .join("");

  const annotations = snapshot.content
    .filter((c) => c.type === "text")
    .flatMap((c) => c.text.annotations);

  return {
    id: snapshot.id,
    role: snapshot.role,
    content: textContent,
    status: snapshot.status,
    annotations: annotations.length > 0 ? annotations : undefined,
    createdAt: new Date(),
  };
}
